import { useEffect, useState } from 'preact/hooks';
import { Fragment } from 'preact/jsx-runtime';
import { useSelect } from 'downshift';
import { Option } from 'interfaces/GlobalConfig.interface';

import { ReactComponent as ChevronDownIcon } from 'icons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from 'icons/chevron-up.svg';
import {
  SelectContainer,
  SelectWrapper,
  SelectLabel,
  SelectToggleButton,
  SelectMenu,
  SelectItem,
  SelectNoResults,
  SelectHelperText,
  SelectValue,
  Container,
} from './Select.styled';

interface SelectProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, 'onSelect'> {
  options: Option[];
  value?: any;
  placeholder?: string;
  hasError?: boolean;
  helpText?: string;
  onSelect?: (value: Option) => void;
}

export const Select = ({
  options = [],
  value = '',
  placeholder = 'Select an option...',
  hasError = false,
  helpText,
  onSelect,
  required,
  ...props
}: SelectProps) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // Item to string conversion for display
  const itemToString = (item: Option | null) => {
    return item ? item.label : '';
  };

  // Find initial selected item
  const getInitialSelectedItem = () => {
    return options.find((option) => option.value === value) || null;
  };

  const {
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    isOpen,
    items: options,
    selectedItem: selectedValue,
    itemToString,
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        setSelectedValue(selectedItem);
        onSelect?.(selectedItem);
      }
    },
    onIsOpenChange: ({ isOpen }) => {
      setIsOpen(isOpen);
    },
  });

  // Update selected item when value prop changes
  useEffect(() => {
    const newSelectedItem = getInitialSelectedItem();
    if (newSelectedItem) {
      setSelectedValue(newSelectedItem);
    }
  }, [value, options]);

  return (
    <Container>
      <SelectContainer
        hasError={hasError}
        {...getToggleButtonProps({
          'aria-required': required,
          'aria-invalid': hasError,
        })}
        {...(props || {})}
      >
        <SelectWrapper>
          <SelectLabel
            {...getLabelProps({
              id: null,
              for: '',
            })}
          >
            <SelectValue isPlaceholder={!selectedValue}>
              {selectedValue ? selectedValue.label : placeholder}
            </SelectValue>
          </SelectLabel>

          <SelectToggleButton>
            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </SelectToggleButton>
        </SelectWrapper>
      </SelectContainer>
      <SelectMenu isOpen={isOpen} {...getMenuProps()}>
        {isOpen && (
          <Fragment>
            {options.length > 0 ? (
              options.map((item, index) => (
                <SelectItem
                  key={item.value}
                  isHighlighted={highlightedIndex === index}
                  isSelected={selectedValue === item}
                  {...getItemProps({
                    item,
                    index,
                  })}
                >
                  {item.label}
                </SelectItem>
              ))
            ) : (
              <SelectNoResults>No Options</SelectNoResults>
            )}
          </Fragment>
        )}
      </SelectMenu>
      {helpText && (
        <SelectHelperText
          isError={hasError}
          size="sm"
          role={hasError ? 'alert' : undefined}
          aria-live={hasError ? 'polite' : undefined}
        >
          {helpText}
        </SelectHelperText>
      )}
    </Container>
  );
};
